import { pick } from 'lodash/fp';
import { createSelector } from 'reselect';
import { array, boolean, nonEmptyArray, option, ord, record, string } from 'fp-ts';
import { NonEmptyArray } from 'fp-ts/NonEmptyArray';
import { flow, identity, pipe } from 'fp-ts/function';
import { LocalizedPropertyObject } from '@mews/framework';
import { getProp } from '@mews/fp';
import { getLanguageCode } from '@mews-commander/core';
import { getReservationsAllNonDefaultProductOrderItems } from '@mews-commander/legacy-entities/src/selectors/orderItems';
import { getProducts } from '@mews-commander/legacy-entities/src/ducks/products';

import { Entities } from '@mews-commander/legacy-entities/src/ducks/utils';
import { getAgeCategories } from '@mews-commander/legacy-entities/src/ducks/ageCategories';
import { ProductOrder, ProductOrderGroup } from './types';
import { createProductOrder } from './createProductOrder';
import { createProductOrderGroup } from './createProductOrderGroup';

export const pickGroupingValues = pick(['data.value.productId', 'isCanceled']);

const getProductOrders = createSelector(getReservationsAllNonDefaultProductOrderItems, array.map(createProductOrder));

export const getSortedProductOrderGroups = createSelector(
    getProductOrders,
    getProducts,
    getAgeCategories,
    () => getLanguageCode(),
    getProp<boolean | undefined>()('withCanceledProductOrders'),
    (orders, products, ageCategories, languageCode, withCanceledProductOrders = true) =>
        array.isNonEmpty(orders)
            ? pipe(
                  orders,
                  withCanceledProductOrders ? identity : array.filter(({ isCanceled }) => !isCanceled),
                  nonEmptyArray.groupBy(flow((order: ProductOrder) => pickGroupingValues(order), JSON.stringify)),
                  record.mapWithIndex((id, groupOrders: ProductOrder[]): ProductOrderGroup => {
                      const ageCategoryGroups: ProductOrderGroup[] = pipe(
                          groupOrders,
                          nonEmptyArray.groupBy((order: ProductOrder) => order.data.value.ageCategoryId ?? ''),
                          record.mapWithIndex((ageCategoryGroupId, ageCategoryGroup: NonEmptyArray<ProductOrder>) => ({
                              ...createProductOrderGroup(ageCategoryGroup, ageCategoryGroupId),
                              ageCategoryId: nonEmptyArray.head(ageCategoryGroup).data.value.ageCategoryId,
                              subgroups: [],
                          })),
                          Object.values<ProductOrderGroup>,
                          array.sortBy([byLocalizedProperty('ageCategoryId', ageCategories, languageCode, 'name')])
                      );
                      return {
                          ...createProductOrderGroup(groupOrders, id),
                          ageCategoryId: null,
                          subgroups: ageCategoryGroups,
                      };
                  }),
                  Object.values<ProductOrderGroup>,
                  array.sortBy([byIsCanceled, byLocalizedProperty('productId', products, languageCode), byConsumption])
              )
            : []
);

const getLocalizedProperty = <T extends { name: LocalizedPropertyObject } | { names: LocalizedPropertyObject }>(
    id: string | null,
    entities: Entities<T>,
    languageCode: string,
    objectProperty: keyof T = 'names' as keyof T
) =>
    pipe(
        id,
        option.fromNullable,
        option.flatMap((value) => record.lookup(value, entities)),
        option.map((entity) => {
            const localizedPropertyObject = entity[objectProperty] as unknown as LocalizedPropertyObject;
            return (
                localizedPropertyObject[languageCode] ??
                nonEmptyArray.head(Object.values(localizedPropertyObject) as NonEmptyArray<string>)
            );
        }),
        option.getOrElseW(() => string.empty)
    );

const byLocalizedProperty = <T extends { name: LocalizedPropertyObject } | { names: LocalizedPropertyObject }>(
    id: 'productId' | 'ageCategoryId',
    allItems: Entities<T>,
    languageCode: string,
    objectProperty: keyof T = 'names' as keyof T
) =>
    pipe(
        string.Ord,
        ord.contramap((group: ProductOrderGroup) =>
            getLocalizedProperty(group[id], allItems, languageCode, objectProperty)
        )
    );

const byIsCanceled = pipe(
    boolean.Ord,
    ord.contramap(({ isCanceled }: ProductOrderGroup) => isCanceled)
);

const byConsumption = pipe(
    string.Ord,
    ord.contramap(({ consumptionUtc }: ProductOrderGroup) => consumptionUtc?.first ?? '0')
);
